<script setup>
import { ref, nextTick, onMounted, onBeforeMount, onBeforeUnmount, watch } from "vue";
import { Store } from "vuex";
import { getQueryParams, removeQueryParam } from "common/utils/browser";
import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";
import canvaModule from "./canvaModule";
import CanvaModal from "./CanvaModal.vue";
import CanvaPortal from "./CanvaPortal.vue";
import CanvaTrigger from "./CanvaTrigger.vue";

const props = defineProps({
  fileLanding: {
    type: String,
    required: true
  },
  namespace: {
    type: String,
    required: true
  },
  replica: {
    type: Boolean,
    default: false
  },
  settings: {
    type: Object,
    default: () => ({})
  },
  skipSettingsLoading: {
    type: Boolean,
    default: false
  },
  skipInitialModalShow: {
    type: Boolean,
    default: false
  }
});

const AUTO_START_QUERY_PARAM = "open_canva_modal_on_start";
const store = new Store();
const modalRender = ref(false);
const triggerId = `canva-button-${props.namespace}`;

const handleCanva = (skipCanvaModal = false) => {
  const args = getQueryParams();
  if (!args[AUTO_START_QUERY_PARAM]) {
    submitDtfEvent("canva_button_click", { namespace: props.namespace });
  }

  const settings = store.getters["canva/settings"];
  if (!settings.authorized) {
    window.location.href = settings.url;
    return;
  }

  modalRender.value = true;
  nextTick(() => {
    if (!skipCanvaModal) {
      jQuery(store.getters["canva/$modal"]).modal("show");
    }

    store.dispatch("canva/resetModalState");
  });
};

const handleProxy = () => {
  document.getElementById(triggerId)?.click();
};

onBeforeMount(() => {
  store.registerModule("canva", canvaModule);
});

onBeforeUnmount(() => {
  store.unregisterModule("canva");
});

watch(
  () => props.settings,
  () => {
    store.commit("canva/setSettings", props.settings);
  }
);

onMounted(async () => {
  if (props.settings) {
    store.commit("canva/setSettings", props.settings);
  }

  if (props.replica) return;

  store.commit("canva/$setFileInput", document.querySelector(props.fileLanding));

  if (!props.skipSettingsLoading) {
    await store.dispatch("canva/fetchSettings", props.namespace);
  }

  const args = getQueryParams();
  const settings = store.getters["canva/settings"];
  if (args[AUTO_START_QUERY_PARAM] && settings.authorized) {
    handleCanva(props.skipInitialModalShow);
    removeQueryParam(AUTO_START_QUERY_PARAM);
    submitDtfEvent("canva_account_linked", { namespace: props.namespace });
  }
});
</script>

<template>
  <template v-if="replica">
    <CanvaTrigger :class="[`canva-trigger--${props.namespace}`]" @click="handleProxy" />
  </template>
  <template v-else>
    <CanvaTrigger
      :id="triggerId"
      :class="[`canva-trigger--${props.namespace}`]"
      @click="handleCanva"
    />
    <CanvaPortal
      v-if="modalRender"
      :component="CanvaModal"
      :props="{
        class: [`canva-modal--${namespace}`],
        store
      }"
    />
  </template>
</template>
